import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  BrainCircuit,
  ChevronDown,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Users,
  X,
} from "lucide-react";
import { TIPS_FILTERS, TIPS_SORT_OPTIONS } from "../desk/tipsDeskHelpers";
import { formatAppTime } from "../../../utils/appTimeFormat";
import FavoriteTeamStar from "../../p2p/FavoriteTeamStar";
import { fixtureHasFavoriteTeam, teamMatchesFavorite } from "../../../utils/p2pHelpers";

const TeamMark = ({ team, favoriteTeam }) => {
  const isFavorite = teamMatchesFavorite(team, favoriteTeam);
  return (
    <span className={`pr-slip-logo ${isFavorite ? "pr-slip-logo-fav" : ""}`} aria-hidden>
      {team?.logo ? <img src={team.logo} alt="" /> : <span>{(team?.name || "?").slice(0, 1)}</span>}
      {isFavorite ? <FavoriteTeamStar size={7} className="pr-slip-logo-star" /> : null}
    </span>
  );
};

/** DraftKings-style compact fixture slip — accent bar + match + dual source picks. */
const FixtureSignal = ({ entry, selected, onSelect, favoriteTeam }) => {
  const home = entry.fixture?.teams?.home || {};
  const away = entry.fixture?.teams?.away || {};
  const showsFavoriteStar = fixtureHasFavoriteTeam(entry.fixture, favoriteTeam);
  const hasAI = Boolean(entry.ai);
  const hasCrowd = (entry.consensus?.count || 0) > 0;
  const aiPick = entry.aiReadout?.pick || (hasAI ? "Locked AI" : "—");
  const readableCrowd = entry.consensus?.readableCount || 0;
  const tipsterAgree = entry.consensus?.agreement || 0;
  const crowdPick = hasCrowd
    ? entry.consensus?.topPick ||
      (readableCrowd === 0 ? `Premium · ${entry.consensus.count}` : "Mixed")
    : "—";
  const crowdPickLabel = hasCrowd
    ? readableCrowd === 0
      ? crowdPick
      : tipsterAgree && entry.consensus?.topPick
        ? `${crowdPick} · ${tipsterAgree}%`
        : `${crowdPick}${entry.consensus.count > 1 ? ` · ${entry.consensus.count}` : ""}`
    : crowdPick;
  const kick = entry.ts ? formatAppTime(new Date(Number(entry.ts) * 1000)) : "TBD";
  const percent = entry.aiReadout?.percent;
  const hasSplit = Boolean(percent && (percent.home || percent.draw || percent.away));
  const live = entry.status?.tone === "live";
  const accent =
    entry.edge === "agree"
      ? "agree"
      : entry.edge === "split"
        ? "split"
        : hasAI
          ? "ai"
          : hasCrowd
            ? "crowd"
            : "none";

  return (
    <button
      type="button"
      className={`pr-slip ${selected ? "pr-slip-on" : ""}`}
      onClick={() => onSelect?.(entry.fixtureId)}
      aria-pressed={selected}
    >
      <i className={`pr-slip-accent pr-slip-accent-${accent}`} aria-hidden />

      <span className="pr-slip-body">
        <span className="pr-slip-line1">
          <em className="pr-slip-league">{entry.league?.name || "League"}</em>
          <span className="pr-slip-dot" aria-hidden>
            ·
          </span>
          <em className="pr-slip-kick">
            {live ? "LIVE" : kick}
            {showsFavoriteStar ? <FavoriteTeamStar size={8} className="pr-rail-kick-fav-star" /> : null}
          </em>
          <span className="pr-slip-score" title="Prediction strength">
            {entry.signal || 0}
          </span>
        </span>

        <span className="pr-slip-match">
          <TeamMark team={home} favoriteTeam={favoriteTeam} />
          <strong className={teamMatchesFavorite(home, favoriteTeam) ? "pr-rail-team-fav" : ""}>
            {home.name || "Home"}
          </strong>
          <i>vs</i>
          <strong className={teamMatchesFavorite(away, favoriteTeam) ? "pr-rail-team-fav" : ""}>
            {away.name || "Away"}
          </strong>
          <TeamMark team={away} favoriteTeam={favoriteTeam} />
        </span>

        {hasSplit ? (
          <span
            className="pr-slip-splitbar"
            aria-hidden
            title={`H ${percent.home}% · D ${percent.draw}% · A ${percent.away}%`}
          >
            <i style={{ width: `${percent.home}%` }} />
            <i style={{ width: `${percent.draw}%` }} />
            <i style={{ width: `${percent.away}%` }} />
          </span>
        ) : null}

        <span className="pr-slip-picks">
          <span className={`pr-slip-pick pr-slip-pick-ai ${hasAI ? "" : "pr-slip-pick-empty"}`}>
            <BrainCircuit size={9} aria-hidden />
            <b>{aiPick}</b>
          </span>
          <span
            className={`pr-slip-pick pr-slip-pick-crowd ${hasCrowd ? "" : "pr-slip-pick-empty"}`}
            title={
              hasCrowd && tipsterAgree
                ? `${tipsterAgree}% tipster agreement on ${entry.consensus?.topPick || "pick"}`
                : undefined
            }
          >
            <Users size={9} aria-hidden />
            <b>{crowdPickLabel}</b>
          </span>
        </span>
      </span>

      <span className="pr-slip-action">
        <span className="pr-slip-cta">Open</span>
      </span>
    </button>
  );
};

const PredictionSignalRail = ({
  sections = [],
  totals,
  filteredCount,
  selectedId,
  onSelect,
  filter,
  onFilterChange,
  sortBy,
  onSortChange,
  searchQuery,
  onSearchChange,
  leagues = [],
  leagueFilter,
  onLeagueChange,
  refreshing,
  onRefresh,
  isLoading,
  hasMore,
  loadingMore,
  onLoadMore,
  isAuthenticated,
  favoriteTeam,
}) => {
  const listRef = useRef(null);
  const selectedRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [filter, leagueFilter, sortBy]);

  useEffect(() => {
    if (selectedRef.current && listRef.current) {
      const row = selectedRef.current.getBoundingClientRect();
      const box = listRef.current.getBoundingClientRect();
      if (row.top < box.top || row.bottom > box.bottom) {
        selectedRef.current.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedId]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;

    const onScroll = () => {
      if (!hasMore || loadingMore || isLoading) return;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining < 140) onLoadMore?.();
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [hasMore, loadingMore, isLoading, onLoadMore]);

  const createTo = isAuthenticated ? "/app/predictions/create" : "/app/login";
  const createState = isAuthenticated ? undefined : { from: "/app/predictions/create" };

  return (
    <aside className="pr-rail" aria-label="Predictions rail">
      <header className="pr-rail-head">
        <div className="pr-title-slip">
          <div className="pr-title-slip-mid">
            <p className="pr-title-slip-eyebrow">Bond Tips</p>
            <div className="pr-title-slip-row">
              <h2>Predictions</h2>
              <span className="pr-title-slip-pill">{filteredCount} fixtures</span>
            </div>
          </div>
          <div className="pr-title-slip-actions">
            <button
              type="button"
              className="pr-title-tile"
              onClick={onRefresh}
              disabled={refreshing || isLoading}
              aria-label="Refresh predictions"
              title="Refresh"
            >
              {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            </button>
          </div>
        </div>

        <Link to={createTo} state={createState} className="pr-rail-create">
          <Plus size={14} aria-hidden />
          Post tip
        </Link>

        {/* AI / Crowd coverage */}
        <div className="pr-tab-slip" aria-label="Coverage">
          <span className="pr-tab-slip-item">
            AI
            <b>{totals?.ai ?? 0}</b>
          </span>
          <span className="pr-tab-slip-item">
            Crowd
            <b>{totals?.crowd ?? 0}</b>
          </span>
        </div>

        <div className="pr-search">
          <Search size={13} aria-hidden />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search team, league, tipster"
            aria-label="Search predictions"
          />
          {searchQuery ? (
            <button type="button" onClick={() => onSearchChange("")} aria-label="Clear search">
              <X size={12} />
            </button>
          ) : null}
        </div>

        <div className="pr-filter-row" role="tablist" aria-label="Prediction filters">
          {TIPS_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              className={filter === f.id ? "pr-filter-on" : ""}
              onClick={() => onFilterChange(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="pr-rail-controls">
          <label>
            <span>Sort</span>
            <select value={sortBy} onChange={(e) => onSortChange(e.target.value)} aria-label="Sort predictions">
              {TIPS_SORT_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown size={11} aria-hidden />
          </label>
          {leagues.length > 1 ? (
            <label>
              <span>League</span>
              <select value={leagueFilter} onChange={(e) => onLeagueChange(e.target.value)} aria-label="Filter by league">
                <option value="">All leagues</option>
                {leagues.map((league) => (
                  <option key={league.id} value={league.id}>
                    {league.name} ({league.count})
                  </option>
                ))}
              </select>
              <ChevronDown size={11} aria-hidden />
            </label>
          ) : null}
        </div>
      </header>

      <div className="pr-rail-list" ref={listRef}>
        {isLoading && sections.length === 0 ? (
          <div className="pr-rail-skeleton">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} />
            ))}
          </div>
        ) : sections.length === 0 ? (
          <div className="pr-rail-empty">
            <strong>No prediction found</strong>
            <span>Try another filter or search.</span>
          </div>
        ) : (
          sections.map((section) => (
            <section key={section.leagueId} className="pr-rail-league">
              <div className="pr-section-eyebrow">
                <button
                  type="button"
                  className="pr-section-eyebrow-btn"
                  onClick={() => onLeagueChange(leagueFilter === section.leagueId ? "" : section.leagueId)}
                >
                  {section.leagueLogo ? <img src={section.leagueLogo} alt="" /> : null}
                  <strong>{section.leagueName}</strong>
                </button>
                <i className="pr-section-rule" aria-hidden />
                <em>{section.fixtures.length}</em>
              </div>
              <div className="pr-rail-league-body">
                {section.fixtures.map((entry) => (
                  <div key={entry.fixtureId} ref={entry.fixtureId === selectedId ? selectedRef : null}>
                    <FixtureSignal
                      entry={entry}
                      selected={entry.fixtureId === selectedId}
                      onSelect={onSelect}
                      favoriteTeam={favoriteTeam}
                    />
                  </div>
                ))}
              </div>
            </section>
          ))
        )}

        {hasMore ? (
          <button type="button" className="pr-load-more" onClick={onLoadMore} disabled={loadingMore || isLoading}>
            {loadingMore ? <Loader2 size={12} className="animate-spin" /> : null}
            {loadingMore ? "Loading more" : "Load more predictions"}
          </button>
        ) : null}
      </div>
    </aside>
  );
};

export default PredictionSignalRail;
